import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Send, Mail, ShieldCheck, Save, Check, EyeOff, Eye, Trash2, Pencil, Star,
  StarOff, Link2, Copy, RefreshCw, Plus, MessageSquare, Settings2, X,
} from "lucide-react";

const OPTIONS = [
  { id: 1, label: "Chci na to zapomenout." },
  { id: 2, label: "Potřebuji terapii." },
  { id: 3, label: "Vy potřebujete terapii!" },
  { id: 4, label: "Bylo to skvělé, bohužel..." },
  { id: 5, label: "Chci podat stížnost a koupit voucher." },
];
const STAR_VALUES = [-1, -2, -3, -4, -5];

function Badge({ children, tone = "neutral" }) {
  const map = { green: "text-nv border-nv/40", amber: "text-amber-400 border-amber-700/40", red: "text-red-400 border-red-800/40", neutral: "text-neutral-400 border-white/15" };
  return <span className={`text-[10px] px-2 py-0.5 border ${map[tone]} tracking-widest uppercase`}>{children}</span>;
}

function NegStars({ value }) {
  const n = Math.abs(value || 0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <StarOff key={i} className={`w-4 h-4 ${i <= n ? "text-nv" : "text-neutral-800"}`} strokeWidth={2} />
      ))}
      <span className="ml-1 text-xs font-head text-nv">−{n}</span>
    </span>
  );
}

/* ---------------- OLD SYSTEM: send emails ---------------- */
function SendTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyPlayed, setOnlyPlayed] = useState(true);
  const [sel, setSel] = useState({});
  const [sending, setSending] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/reviews/recipients").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const list = rows.filter((r) => (onlyPlayed ? r.played : true));
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const toggle = (id) => setSel((p) => ({ ...p, [id]: !p[id] }));
  const allChecked = list.length > 0 && list.every((r) => sel[r.id]);
  const toggleAll = () => { const n = {}; if (!allChecked) list.forEach((r) => { n[r.id] = true; }); setSel(n); };

  const send = async () => {
    if (selectedIds.length === 0) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/reviews/send", { reservation_ids: selectedIds });
      const n = data.sent;
      const word = n === 1 ? "e-mail" : (n >= 2 && n <= 4 ? "e-maily" : "e-mailů");
      toast.success(`Odesláno ${n} ${word} s hodnocením.`);
      setSel({}); load();
    } catch (e) { toast.error("Odeslání selhalo."); }
    finally { setSending(false); }
  };

  if (loading) return <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;

  return (
    <div className="space-y-4" data-testid="reviews-send">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
          <input type="checkbox" checked={onlyPlayed} onChange={(e) => setOnlyPlayed(e.target.checked)} data-testid="filter-played" /> Jen odehrané
        </label>
        <button onClick={send} disabled={sending || selectedIds.length === 0} data-testid="send-reviews-btn"
          className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head disabled:opacity-40">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Odeslat vybraným ({selectedIds.length})
        </button>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0d0d0d] text-neutral-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="p-3"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="select-all" /></th>
              <th className="p-3 text-left">Zákazník</th>
              <th className="p-3 text-left">Termín</th>
              <th className="p-3 text-left">Stav</th>
              <th className="p-3 text-left">Hodnocení</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} className="border-t border-white/5" data-testid={`recipient-${r.id}`}>
                <td className="p-3"><input type="checkbox" checked={!!sel[r.id]} onChange={() => toggle(r.id)} data-testid={`check-${r.id}`} /></td>
                <td className="p-3"><span className="text-neutral-200">{r.first_name} {r.last_name}</span><br /><span className="text-xs text-neutral-500">{r.email}</span></td>
                <td className="p-3 text-neutral-400">{r.date} · {r.time}</td>
                <td className="p-3">{r.played ? <Badge tone="green">hrál</Badge> : <Badge tone="amber">čeká</Badge>}</td>
                <td className="p-3">
                  {r.has_review ? <Badge tone="green">ohodnoceno</Badge> : r.review_sent_at ? <Badge tone="neutral">odesláno</Badge> : <span className="text-xs text-neutral-600">—</span>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-neutral-600 text-xs">Žádní zákazníci.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- OLD SYSTEM: moderation ---------------- */
function ModerationTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const [editOpt, setEditOpt] = useState(1);

  const load = () => { setLoading(true); api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const patch = async (id, body) => { try { await api.put(`/admin/reviews/${id}`, body); load(); } catch (e) { toast.error("Změna selhala."); } };
  const del = async (id) => { if (!window.confirm("Opravdu smazat toto hodnocení?")) return; try { await api.delete(`/admin/reviews/${id}`); toast.success("Smazáno."); load(); } catch (e) { toast.error("Smazání selhalo."); } };
  const startEdit = (r) => { setEditing(r.id); setEditText(r.text || ""); setEditOpt(r.option_id); };
  const saveEdit = async (id) => { await patch(id, { text: editText, option_id: editOpt }); setEditing(null); toast.success("Uloženo."); };

  if (loading) return <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;

  return (
    <div className="space-y-3" data-testid="reviews-moderation">
      {reviews.length === 0 && <p className="text-neutral-600 text-sm p-4 border border-dashed border-white/10 text-center">Zatím žádná hodnocení.</p>}
      {reviews.map((r) => (
        <div key={r.id} className="border border-white/10 bg-[#0b0a09] p-4" data-testid={`review-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-head text-neutral-200">{r.first_name || "Anonym"}</span>
                {r.approved ? <Badge tone="green">schváleno</Badge> : <Badge tone="amber">čeká</Badge>}
                {r.hidden && <Badge tone="red">skryto</Badge>}
              </div>
              <p className="text-sm text-nv font-head">{r.option_label}</p>
              {editing === r.id ? (
                <div className="mt-2 space-y-2">
                  <select value={editOpt} onChange={(e) => setEditOpt(Number(e.target.value))} className="bg-black border border-white/15 text-neutral-200 text-sm p-2 w-full" data-testid={`edit-option-${r.id}`}>
                    {OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="rounded-none bg-black border-white/15 text-sm" data-testid={`edit-text-${r.id}`} />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(r.id)} className="hud-btn vhs-btn px-3 py-1.5 text-xs flex items-center gap-1"><Save className="w-3 h-3" /> Uložit</button>
                    <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs text-neutral-500 border border-white/10">Zrušit</button>
                  </div>
                </div>
              ) : (
                r.text ? <p className="text-sm text-neutral-400 mt-1 whitespace-pre-line">„{r.text}"</p> : <p className="text-xs text-neutral-600 mt-1">bez komentáře</p>
              )}
            </div>
            {editing !== r.id && (
              <div className="flex items-center gap-1 shrink-0">
                {!r.approved && <button onClick={() => patch(r.id, { approved: true, hidden: false })} title="Schválit" data-testid={`approve-${r.id}`} className="p-2 text-nv hover:bg-nv/10 border border-nv/30"><Check className="w-4 h-4" /></button>}
                <button onClick={() => patch(r.id, { hidden: !r.hidden })} title={r.hidden ? "Zobrazit" : "Skrýt"} data-testid={`hide-${r.id}`} className="p-2 text-neutral-400 hover:text-neutral-100 border border-white/10">{r.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                <button onClick={() => startEdit(r)} title="Upravit" data-testid={`edit-${r.id}`} className="p-2 text-neutral-400 hover:text-neutral-100 border border-white/10"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(r.id)} title="Smazat" data-testid={`delete-${r.id}`} className="p-2 text-red-400 hover:bg-red-950/30 border border-red-900/40"><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- OLD SYSTEM: email template ---------------- */
function TemplateTab() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setSubject(data.review_subject || ""); setBody(data.review_body || ""); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { review_subject: subject, review_body: body }); toast.success("Text e-mailu uložen."); }
    catch (e) { toast.error("Uložení selhalo."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;

  return (
    <div className="border border-white/10 bg-[#0b0a09] p-5 space-y-4 max-w-2xl" data-testid="reviews-template">
      <p className="text-xs text-neutral-500">Zástupné značky: <code className="text-nv">{"{jmeno}"}</code> = jméno zákazníka, <code className="text-nv">{"{odkaz}"}</code> = odkaz na hodnocení.</p>
      <div>
        <label className="text-xs text-neutral-400">Předmět</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="review-subject" className="rounded-none bg-black border-white/15 text-sm mt-1" />
      </div>
      <div>
        <label className="text-xs text-neutral-400">Text e-mailu</label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} data-testid="review-body" className="rounded-none bg-black border-white/15 text-sm mt-1" />
      </div>
      <button onClick={save} disabled={saving} data-testid="save-review-template" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit text
      </button>
    </div>
  );
}

/* ---------------- NEW SYSTEM: negative reviews moderation + owner create + reply ---------------- */
function NegModerationTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [cNick, setCNick] = useState("");
  const [cStars, setCStars] = useState(-3);
  const [cText, setCText] = useState("");
  const [creating, setCreating] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [editing, setEditing] = useState(null);
  const [eNick, setENick] = useState("");
  const [eText, setEText] = useState("");
  const [eStars, setEStars] = useState(-3);

  const load = () => { setLoading(true); api.get("/admin/reviews2").then(({ data }) => setReviews(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const patch = async (id, body) => { try { await api.put(`/admin/reviews2/${id}`, body); load(); } catch (e) { toast.error("Změna selhala."); } };
  const del = async (id) => { if (!window.confirm("Opravdu smazat tuto recenzi?")) return; try { await api.delete(`/admin/reviews2/${id}`); toast.success("Smazáno."); load(); } catch (e) { toast.error("Smazání selhalo."); } };

  const create = async () => {
    setCreating(true);
    try { await api.post("/admin/reviews2", { nickname: cNick, stars: cStars, text: cText }); toast.success("Recenze vytvořena a zveřejněna."); setCNick(""); setCText(""); setCStars(-3); setShowCreate(false); load(); }
    catch (e) { toast.error("Vytvoření selhalo."); }
    finally { setCreating(false); }
  };

  const startEdit = (r) => { setEditing(r.id); setENick(r.nickname || ""); setEText(r.text || ""); setEStars(r.stars || -3); };
  const saveEdit = async (id) => { await patch(id, { nickname: eNick || "Anonym", text: eText, stars: eStars }); setEditing(null); toast.success("Uloženo."); };
  const saveReply = async (id) => { await patch(id, { reply: replyText }); setReplyOpen(null); setReplyText(""); toast.success("Odpověď uložena."); };

  const StarSelect = ({ value, onChange, testid }) => (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} className="bg-black border border-white/15 text-neutral-200 text-sm p-2">
      {STAR_VALUES.map((v) => <option key={v} value={v}>−{Math.abs(v)} hvězd</option>)}
    </select>
  );

  if (loading) return <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;

  return (
    <div className="space-y-3" data-testid="reviews2-moderation">
      <div className="flex justify-end">
        <button onClick={() => setShowCreate((v) => !v)} data-testid="neg-create-toggle" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head">
          {showCreate ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showCreate ? "Zavřít" : "Přidat vlastní recenzi"}
        </button>
      </div>

      {showCreate && (
        <div className="border border-nv/30 bg-[#0b0a09] p-4 space-y-3" data-testid="neg-create-form">
          <p className="text-xs text-neutral-500">Vytvoř recenzi jako majitel. Rovnou se zveřejní (schváleno).</p>
          <div className="flex gap-3 flex-wrap items-center">
            <Input value={cNick} onChange={(e) => setCNick(e.target.value)} placeholder="Přezdívka (nepovinné)" data-testid="neg-create-nick" className="rounded-none bg-black border-white/15 text-sm max-w-xs" />
            <StarSelect value={cStars} onChange={setCStars} testid="neg-create-stars" />
            <NegStars value={cStars} />
          </div>
          <Textarea value={cText} onChange={(e) => setCText(e.target.value)} rows={3} placeholder="Text recenze…" data-testid="neg-create-text" className="rounded-none bg-black border-white/15 text-sm" />
          <button onClick={create} disabled={creating} data-testid="neg-create-submit" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Vytvořit a zveřejnit
          </button>
        </div>
      )}

      {reviews.length === 0 && <p className="text-neutral-600 text-sm p-4 border border-dashed border-white/10 text-center">Zatím žádné negativní recenze.</p>}
      {reviews.map((r) => (
        <div key={r.id} className="border border-white/10 bg-[#0b0a09] p-4" data-testid={`neg-review-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-head text-neutral-200">{r.nickname || "Anonym"}</span>
                {r.approved ? <Badge tone="green">schváleno</Badge> : <Badge tone="amber">čeká</Badge>}
                {r.hidden && <Badge tone="red">skryto</Badge>}
                {r.source === "owner" && <Badge tone="neutral">majitel</Badge>}
              </div>
              {editing === r.id ? (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-3 flex-wrap items-center">
                    <Input value={eNick} onChange={(e) => setENick(e.target.value)} placeholder="Přezdívka" className="rounded-none bg-black border-white/15 text-sm max-w-xs" data-testid={`neg-edit-nick-${r.id}`} />
                    <StarSelect value={eStars} onChange={setEStars} testid={`neg-edit-stars-${r.id}`} />
                  </div>
                  <Textarea value={eText} onChange={(e) => setEText(e.target.value)} rows={2} className="rounded-none bg-black border-white/15 text-sm" data-testid={`neg-edit-text-${r.id}`} />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(r.id)} className="hud-btn vhs-btn px-3 py-1.5 text-xs flex items-center gap-1"><Save className="w-3 h-3" /> Uložit</button>
                    <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs text-neutral-500 border border-white/10">Zrušit</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-1"><NegStars value={r.stars} /></div>
                  {r.text ? <p className="text-sm text-neutral-400 mt-1 whitespace-pre-line">„{r.text}"</p> : <p className="text-xs text-neutral-600 mt-1">bez komentáře</p>}
                  {r.reply ? <div className="mt-2 border-l-2 border-nv/40 pl-3 text-sm text-neutral-400"><span className="text-[10px] tracking-widest text-nv uppercase block">odpověď</span>{r.reply}</div> : null}
                </>
              )}

              {replyOpen === r.id && (
                <div className="mt-3 space-y-2" data-testid={`neg-reply-form-${r.id}`}>
                  <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} placeholder="Odpověď majitele…" className="rounded-none bg-black border-white/15 text-sm" data-testid={`neg-reply-text-${r.id}`} />
                  <div className="flex gap-2">
                    <button onClick={() => saveReply(r.id)} className="hud-btn vhs-btn px-3 py-1.5 text-xs flex items-center gap-1"><Save className="w-3 h-3" /> Uložit odpověď</button>
                    <button onClick={() => { setReplyOpen(null); setReplyText(""); }} className="px-3 py-1.5 text-xs text-neutral-500 border border-white/10">Zrušit</button>
                  </div>
                </div>
              )}
            </div>
            {editing !== r.id && (
              <div className="flex items-center gap-1 shrink-0">
                {!r.approved && <button onClick={() => patch(r.id, { approved: true, hidden: false })} title="Schválit" data-testid={`neg-approve-${r.id}`} className="p-2 text-nv hover:bg-nv/10 border border-nv/30"><Check className="w-4 h-4" /></button>}
                <button onClick={() => { setReplyOpen(r.id); setReplyText(r.reply || ""); }} title="Odpovědět" data-testid={`neg-reply-${r.id}`} className="p-2 text-neutral-400 hover:text-neutral-100 border border-white/10"><MessageSquare className="w-4 h-4" /></button>
                <button onClick={() => patch(r.id, { hidden: !r.hidden })} title={r.hidden ? "Zobrazit" : "Skrýt"} data-testid={`neg-hide-${r.id}`} className="p-2 text-neutral-400 hover:text-neutral-100 border border-white/10">{r.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                <button onClick={() => startEdit(r)} title="Upravit" data-testid={`neg-edit-${r.id}`} className="p-2 text-neutral-400 hover:text-neutral-100 border border-white/10"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(r.id)} title="Smazat" data-testid={`neg-delete-${r.id}`} className="p-2 text-red-400 hover:bg-red-950/30 border border-red-900/40"><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- NEW SYSTEM: settings (visibility, link, texts, templates) ---------------- */
function NegSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [oldVisible, setOldVisible] = useState(true);
  const [newVisible, setNewVisible] = useState(false);
  const [sectionVis, setSectionVis] = useState({});
  const [title, setTitle] = useState("");
  const [kicker, setKicker] = useState("");
  const [intro, setIntro] = useState("");
  const [thanks, setThanks] = useState("");
  const [templates, setTemplates] = useState([]);
  const [token, setToken] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [nickLabel, setNickLabel] = useState("");
  const [nickPlaceholder, setNickPlaceholder] = useState("");
  const [starsLabel, setStarsLabel] = useState("");
  const [starLabels, setStarLabels] = useState(["", "", "", "", ""]);
  const [textLabel, setTextLabel] = useState("");
  const [textPlaceholder, setTextPlaceholder] = useState("");
  const [submitLabel, setSubmitLabel] = useState("");
  const [note, setNote] = useState("");
  const [perPage, setPerPage] = useState(3);

  useEffect(() => {
    Promise.all([api.get("/admin/settings"), api.get("/admin/reviews2/link")])
      .then(([{ data }, { data: link }]) => {
        const sv = data.section_visibility || {};
        setSectionVis(sv);
        setOldVisible(sv.reviews !== false);
        setNewVisible(!!data.reviews_neg_visible);
        setTitle(data.reviews_neg_title || "");
        setKicker(data.reviews_neg_kicker || "");
        setIntro(data.reviews_neg_intro || "");
        setThanks(data.reviews_neg_thanks || "");
        setTemplates(data.reviews_neg_templates || []);
        setToken(link.reviews_neg_link_token || "");
        setSubtitle(data.reviews_neg_subtitle || "");
        setNickLabel(data.reviews_neg_nick_label || "");
        setNickPlaceholder(data.reviews_neg_nick_placeholder || "");
        setStarsLabel(data.reviews_neg_stars_label || "");
        setStarLabels((data.reviews_neg_star_labels && data.reviews_neg_star_labels.length === 5) ? data.reviews_neg_star_labels : ["", "", "", "", ""]);
        setTextLabel(data.reviews_neg_text_label || "");
        setTextPlaceholder(data.reviews_neg_text_placeholder || "");
        setSubmitLabel(data.reviews_neg_submit_label || "");
        setNote(data.reviews_neg_note || "");
        setPerPage(data.reviews_neg_per_page || 3);
      })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const link = token ? `${window.location.origin}/recenze/${token}` : "";

  const save = async () => {
    setSaving(true);
    try {
      const sv = { ...sectionVis, reviews: oldVisible };
      await api.put("/admin/settings", {
        section_visibility: sv,
        reviews_neg_visible: newVisible,
        reviews_neg_title: title,
        reviews_neg_kicker: kicker,
        reviews_neg_intro: intro,
        reviews_neg_thanks: thanks,
        reviews_neg_templates: templates,
        reviews_neg_subtitle: subtitle,
        reviews_neg_nick_label: nickLabel,
        reviews_neg_nick_placeholder: nickPlaceholder,
        reviews_neg_stars_label: starsLabel,
        reviews_neg_star_labels: starLabels,
        reviews_neg_text_label: textLabel,
        reviews_neg_text_placeholder: textPlaceholder,
        reviews_neg_submit_label: submitLabel,
        reviews_neg_note: note,
        reviews_neg_per_page: Number(perPage) || 3,
      });
      toast.success("Nastavení uloženo.");
    } catch (e) { toast.error("Uložení selhalo."); }
    finally { setSaving(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(link).then(() => toast.success("Odkaz zkopírován.")).catch(() => toast.error("Kopírování selhalo.")); };
  const regen = async () => {
    if (!window.confirm("Vygenerovat nový odkaz? Starý přestane fungovat.")) return;
    try { const { data } = await api.post("/admin/reviews2/regenerate-link"); setToken(data.reviews_neg_link_token); toast.success("Nový odkaz vygenerován."); }
    catch (e) { toast.error("Generování selhalo."); }
  };

  // template editing
  const addArea = () => setTemplates((t) => [...t, { area: "Nová oblast", variants: [""] }]);
  const removeArea = (i) => setTemplates((t) => t.filter((_, idx) => idx !== i));
  const setArea = (i, v) => setTemplates((t) => t.map((a, idx) => idx === i ? { ...a, area: v } : a));
  const addVariant = (i) => setTemplates((t) => t.map((a, idx) => idx === i ? { ...a, variants: [...(a.variants || []), ""] } : a));
  const setVariant = (i, vi, v) => setTemplates((t) => t.map((a, idx) => idx === i ? { ...a, variants: a.variants.map((x, xi) => xi === vi ? v : x) } : a));
  const removeVariant = (i, vi) => setTemplates((t) => t.map((a, idx) => idx === i ? { ...a, variants: a.variants.filter((_, xi) => xi !== vi) } : a));

  if (loading) return <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="reviews2-settings">
      {/* visibility */}
      <div className="border border-white/10 bg-[#0b0a09] p-5 space-y-3">
        <h3 className="font-head text-lg text-neutral-100">Zobrazení sekcí recenzí</h3>
        <label className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2 bg-black" data-testid="toggle-old-reviews">
          <span className="text-sm text-neutral-300">Starý systém recenzí (5 reakcí, e-mailem)</span>
          <Switch checked={oldVisible} onCheckedChange={setOldVisible} />
        </label>
        <label className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2 bg-black" data-testid="toggle-new-reviews">
          <span className="text-sm text-neutral-300">Nový systém — negativní recenze (mínus hvězdy)</span>
          <Switch checked={newVisible} onCheckedChange={setNewVisible} />
        </label>
        <div>
          <label className="text-xs text-neutral-400">Počet recenzí na stránku (pak se listuje šipkou → doprava)</label>
          <Input type="number" min={1} max={20} value={perPage} onChange={(e) => setPerPage(e.target.value)} data-testid="neg-per-page" className="rounded-none bg-black border-white/15 text-sm mt-1 w-32" />
        </div>
      </div>

      {/* shared link */}
      <div className="border border-white/10 bg-[#0b0a09] p-5 space-y-3">
        <h3 className="font-head text-lg text-neutral-100 flex items-center gap-2"><Link2 className="w-4 h-4 text-nv" /> Sdílený odkaz pro zákazníky</h3>
        <p className="text-xs text-neutral-500">Zkopíruj odkaz a pošli ho zákazníkům. Přes něj mohou přidat negativní recenzi. Recenze pak schválíš v záložce „Neg. moderace".</p>
        <div className="flex gap-2 flex-wrap">
          <Input value={link} readOnly data-testid="neg-link-input" className="rounded-none bg-black border-white/15 text-sm flex-1 min-w-[240px]" />
          <button onClick={copyLink} data-testid="neg-copy-link" className="hud-btn vhs-btn flex items-center gap-2 px-3 py-2 text-sm"><Copy className="w-4 h-4" /> Kopírovat</button>
          <button onClick={regen} data-testid="neg-regen-link" className="flex items-center gap-2 px-3 py-2 text-sm text-amber-400 border border-amber-700/40 hover:bg-amber-950/20"><RefreshCw className="w-4 h-4" /> Nový odkaz</button>
        </div>
      </div>

      {/* texts */}
      <div className="border border-white/10 bg-[#0b0a09] p-5 space-y-3">
        <h3 className="font-head text-lg text-neutral-100">Texty sekce a formuláře</h3>
        <div>
          <label className="text-xs text-neutral-400">Nadpis sekce</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="neg-title" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
        <div>
          <label className="text-xs text-neutral-400">Kicker (malý text nad nadpisem)</label>
          <Input value={kicker} onChange={(e) => setKicker(e.target.value)} data-testid="neg-kicker" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
        <div>
          <label className="text-xs text-neutral-400">Popis / vysvětlení (že mohou napsat negativní recenzi)</label>
          <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3} data-testid="neg-intro" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
        <div>
          <label className="text-xs text-neutral-400">Poděkování po odeslání</label>
          <Textarea value={thanks} onChange={(e) => setThanks(e.target.value)} rows={2} data-testid="neg-thanks" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
      </div>

      {/* form texts (link module) */}
      <div className="border border-white/10 bg-[#0b0a09] p-5 space-y-3" data-testid="neg-form-texts">
        <h3 className="font-head text-lg text-neutral-100">Texty formuláře (co vidí zákazník přes odkaz)</h3>
        <p className="text-xs text-neutral-500">Tyto texty se zobrazí v modulu, který zákazníci dostanou přes sdílený odkaz.</p>
        <div>
          <label className="text-xs text-neutral-400">Podnadpis (pod nadpisem)</label>
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} data-testid="neg-subtitle" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-neutral-400">Popisek pole „Přezdívka"</label>
            <Input value={nickLabel} onChange={(e) => setNickLabel(e.target.value)} data-testid="neg-nick-label" className="rounded-none bg-black border-white/15 text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs text-neutral-400">Placeholder přezdívky</label>
            <Input value={nickPlaceholder} onChange={(e) => setNickPlaceholder(e.target.value)} data-testid="neg-nick-placeholder" className="rounded-none bg-black border-white/15 text-sm mt-1" />
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-400">Popisek výběru hvězd</label>
          <Input value={starsLabel} onChange={(e) => setStarsLabel(e.target.value)} data-testid="neg-stars-label" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
        <div>
          <label className="text-xs text-neutral-400">Názvy hodnocení (−1 až −5)</label>
          <div className="space-y-2 mt-1">
            {starLabels.map((sl, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-head text-nv w-8 shrink-0">−{i + 1}</span>
                <Input value={sl} onChange={(e) => setStarLabels((a) => a.map((x, xi) => (xi === i ? e.target.value : x)))} data-testid={`neg-star-label-${i}`} className="rounded-none bg-black border-white/15 text-sm" />
              </div>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-neutral-400">Popisek pole „Text recenze"</label>
            <Input value={textLabel} onChange={(e) => setTextLabel(e.target.value)} data-testid="neg-text-label" className="rounded-none bg-black border-white/15 text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs text-neutral-400">Placeholder textu recenze</label>
            <Input value={textPlaceholder} onChange={(e) => setTextPlaceholder(e.target.value)} data-testid="neg-text-placeholder" className="rounded-none bg-black border-white/15 text-sm mt-1" />
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-400">Text tlačítka odeslání</label>
          <Input value={submitLabel} onChange={(e) => setSubmitLabel(e.target.value)} data-testid="neg-submit-label" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
        <div>
          <label className="text-xs text-neutral-400">Poznámka pod tlačítkem</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="neg-note" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
      </div>

      {/* templates */}
      <div className="border border-white/10 bg-[#0b0a09] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-head text-lg text-neutral-100">Předpřipravené texty</h3>
          <button onClick={addArea} data-testid="neg-add-area" className="flex items-center gap-1 text-xs text-nv border border-nv/30 px-2 py-1 hover:bg-nv/10"><Plus className="w-3 h-3" /> Oblast</button>
        </div>
        <p className="text-xs text-neutral-500">Návštěvník vybere oblast (např. hluk), rozbalí variantu a text se mu vloží do recenze.</p>
        {templates.map((a, i) => (
          <div key={i} className="border border-white/10 p-3 space-y-2 bg-black/40" data-testid={`neg-area-${i}`}>
            <div className="flex items-center gap-2">
              <Input value={a.area} onChange={(e) => setArea(i, e.target.value)} data-testid={`neg-area-name-${i}`} className="rounded-none bg-black border-white/15 text-sm font-head" />
              <button onClick={() => removeArea(i)} data-testid={`neg-remove-area-${i}`} className="p-2 text-red-400 border border-red-900/40 hover:bg-red-950/30"><Trash2 className="w-4 h-4" /></button>
            </div>
            {(a.variants || []).map((v, vi) => (
              <div key={vi} className="flex items-center gap-2">
                <Textarea value={v} onChange={(e) => setVariant(i, vi, e.target.value)} rows={2} data-testid={`neg-variant-${i}-${vi}`} className="rounded-none bg-black border-white/15 text-sm" />
                <button onClick={() => removeVariant(i, vi)} data-testid={`neg-remove-variant-${i}-${vi}`} className="p-2 text-red-400 border border-red-900/40 hover:bg-red-950/30 shrink-0"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => addVariant(i)} data-testid={`neg-add-variant-${i}`} className="flex items-center gap-1 text-xs text-neutral-400 border border-white/10 px-2 py-1 hover:text-neutral-100"><Plus className="w-3 h-3" /> Varianta</button>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="save-neg-settings" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit nastavení
      </button>
    </div>
  );
}

export default function AdminReviews() {
  const [tab, setTab] = useState("rozeslani");
  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`reviews-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-head border ${tab === id ? "bg-red-700 text-white border-red-700" : "border-white/10 text-neutral-400 hover:text-neutral-100"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return (
    <div className="space-y-5" data-testid="admin-reviews">
      <div className="flex gap-2 flex-wrap">
        <TabBtn id="rozeslani" icon={Mail} label="Rozeslání (staré)" />
        <TabBtn id="moderace" icon={ShieldCheck} label="Moderace (staré)" />
        <TabBtn id="text" icon={Star} label="Text e-mailu" />
        <TabBtn id="neg" icon={StarOff} label="Neg. moderace" />
        <TabBtn id="neg-nastaveni" icon={Settings2} label="Neg. nastavení" />
      </div>
      {tab === "rozeslani" && <SendTab />}
      {tab === "moderace" && <ModerationTab />}
      {tab === "text" && <TemplateTab />}
      {tab === "neg" && <NegModerationTab />}
      {tab === "neg-nastaveni" && <NegSettingsTab />}
    </div>
  );
}
